import { Away, settleLooks, voiceOfAppearance, type HeldLookChange, type SnapshotRecord } from "odyssey-shared";
import { mayRename, playerNames } from "./player-names.ts";
import { gameReport } from "./report.ts";
import type { SessionParts } from "./session-types.ts";
import type { SessionCrossing } from "./session-crossing.ts";
import { ownClock } from "./own-clock.ts";

/**
 * Makes the handler of the applied snapshots, which reconciles the predictor with the server's state for the local
 * player and records the view for interpolation. The looks that waited for a snapshot are settled against the view it
 * left (ADR-094 decision 6), and each figure whose handle's look changed is told: the figure drawn for it, the page's
 * own or another player's; a handle with no figure yet is told its look as the figure is made. The page's own voice is
 * read from its own record's appearance first (ADR-085's amendment for P1.9-T83). Every thing the record carries is
 * handed to the transition playback first, and the things are redrawn and restamped before the reconcile, so the
 * inputs it replays meet the footprints of the states this snapshot carries rather than the ones before it. The
 * page's moderation and the clock its own timers count on (`own-clock.ts`) are told whether the own character reads
 * held, and the selection follows the view after it. The
 * handler and its look listener are made once and allocate nothing as a snapshot is applied.
 *
 * @param parts The session's parts.
 * @param crossing The page's side of seamless travel, which holds the figure of each `crossed` record before the owning collection forgets it (ADR-147 decision 15).
 * @returns The handler, given each applied snapshot.
 */
export function snapshotReceiver(parts: SessionParts, crossing: SessionCrossing): (record: SnapshotRecord) => void {
  const { options, host, state, connection, local, remotes, fight, targets, ground, looks } = parts;
  const lookChanged: HeldLookChange = (handle, look) => {
    const figure = handle === state.handle ? local.sprite : remotes.drawn.get(handle)?.sprite;
    if (figure !== null && figure !== undefined) {
      host.wear?.(figure, look);
    }
  };
  return (record) => {
    const current = connection();
    if (current === null) {
      return;
    }
    const entities = current.view.entities;
    const sounds = options.sounds;
    const own = sounds === undefined ? undefined : entities.get(state.handle);
    if (sounds !== undefined && own !== undefined) {
      sounds.ownVoice = voiceOfAppearance(own.appearance);
    }
    const world = host.world();
    if (world !== null) {
      world.things.observe(record, entities, host.now());
      world.things.sync(entities);
    }
    ground.snapshot(entities);
    if (mayRename(record)) {
      gameReport.players = playerNames(entities);
    }
    settleLooks(looks, record.tick, record.keyframe, entities, lookChanged);
    if (local.prediction === null || !remotes.active) {
      return;
    }
    state.snapshotSeen = true;
    const tick = record.tick;
    const me = entities.get(state.handle);
    if (me !== undefined) {
      local.reconcile(record.ackSeq, { x: me.x, y: me.y, facing: me.facing }, { x: me.offsetX ?? 0, y: me.offsetY ?? 0 });
    }
    fight.acknowledge(tick, record.ackSeq);
    const held = me?.away === Away.Held;
    ownClock.hold(held, performance.now());
    options.moderation?.held(held);
    targets.follow(entities);
    crossing.snapshot(record, entities);
    remotes.record(tick, entities, state.handle);
    gameReport.snapshots += 1;
    gameReport.tick = tick;
  };
}
